// instructionQualityViewModel.js — Task 6 (AI-assisted task quality control) pure view-model.
//
// PURE: no React, no fetch. NewAgentForm owns the debounce timer, the network call (postData), and
// the chip render; this module owns the deterministic decisions around it so they are node-testable:
//   - shouldScore   — is the draft substantial enough to spend a score on?
//   - draftKey      — a stable key for client-side memo (identical drafts never re-fire)
//   - buildRatePayload — the POST body for /agent_jobs/rate (derives trigger_spec via the SAME
//                        buildAgentJobPayload the submit path uses — never a divergent copy)
//   - normalizeRateResponse — the single fail-open funnel: any malformed response → {off:true}
//
// FAIL-OPEN is concentrated in normalizeRateResponse: the chip is advisory, so anything we don't
// fully understand becomes invisible ({off:true}), never an error state.
import { buildAgentJobPayload } from "./triggeredListViewModel.js"

// A draft shorter than this isn't worth a score (and keeps the API quiet while the user types the
// first few words). DEBOUNCE_MS is the settle window NewAgentForm waits after the last keystroke.
export const MIN_ACTION_CHARS = 25
export const DEBOUNCE_MS = 1500

// Chip copy per rating. Colors live in agent_shell.scss (SCSS pipeline, not JS style objects).
export const RATING_META = Object.freeze({
  green: { label: "Looks solid" },
  yellow: { label: "Could be clearer" },
  red: { label: "Needs work" },
})

// Score only once the instruction text has real substance.
export function shouldScore(draft) {
  return String((draft && draft.action) || "").trim().length >= MIN_ACTION_CHARS
}

// Derive the effective trigger_spec by REUSING buildAgentJobPayload's ok-branch. An unsupported
// trigger (monthly schedule, unknown condition, …) returns ok:false → null here — that draft is
// still worth scoring (the chip can explain the mismatch), so null is a valid, scorable value.
export function resolveTriggerSpec(draft) {
  const d = draft || {}
  const res = buildAgentJobPayload({
    name: d.name,
    kind: d.kind,
    icon: d.icon,
    action: d.action,
    scope: d.scope,
    cfg: d.cfg,
    apiCondition: d.apiCondition,
    rawTrigger: d.rawTrigger,
  })
  return res && res.ok ? res.body.agent_job.trigger_spec : null
}

// Deterministic JSON with recursively-sorted object keys — so two drafts that differ only in key
// insertion order (or trigger_spec key order) collapse to the SAME memo key, while any real content
// change (an action edit, a scope toggle) produces a different key.
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`
  if (value && typeof value === "object") {
    const keys = Object.keys(value).sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`
  }
  return JSON.stringify(value === undefined ? null : value)
}

// Stable memo key for a draft — {name, action, kind, trigger_spec, scope}. Identical drafts never
// re-fire; collapses blur/focus re-renders. Stale-response guarding in NewAgentForm compares this.
export function draftKey(draft) {
  const d = draft || {}
  return stableStringify({
    action: String(d.action || ""),
    kind: String(d.kind || ""),
    name: String(d.name || ""),
    scope: Array.isArray(d.scope) ? d.scope.slice() : [],
    trigger_spec: resolveTriggerSpec(d),
  })
}

// The POST body for /agent_jobs/rate. context defaults to "agent_job"; the task-9 quick-task seam
// passes "quick_task" (dormant tonight — nothing consumes it yet).
export function buildRatePayload(draft) {
  const d = draft || {}
  return {
    draft: {
      name: String(d.name || ""),
      action: String(d.action || ""),
      kind: String(d.kind || ""),
      scope: Array.isArray(d.scope) ? d.scope.slice() : [],
      trigger_spec: resolveTriggerSpec(d),
      context: d.context === "quick_task" ? "quick_task" : "agent_job",
    },
  }
}

// The single fail-open funnel. Returns {off:true} unless the response is a well-formed, available
// score; otherwise {rating, reasons (clamped to 4), optimized (non-blank string or null)}.
export function normalizeRateResponse(json) {
  if (!json || json.available !== true) return { off: true }
  const rating = json.rating
  if (rating !== "green" && rating !== "yellow" && rating !== "red") return { off: true }
  const reasons = json.reasons
  if (!Array.isArray(reasons) || reasons.length === 0 || !reasons.every((r) => typeof r === "string")) {
    return { off: true }
  }
  const optimized =
    typeof json.optimized_version === "string" && json.optimized_version.trim() !== ""
      ? json.optimized_version
      : null
  return { rating, reasons: reasons.slice(0, 4), optimized }
}
